import type {
  Application,
  CandidateProfile,
  EmployerDocument,
  EmployerProfile,
  Job,
  Testimonial,
} from '@/api/types';
import type { MockDb, MockUser } from './db';

/** Demo email addresses surfaced by the login panel. */
export const DEMO_EMAILS = {
  candidate: 'demo.candidate@example.com',
  employer: 'demo.employer@example.com',
  admin: 'alwarrojarsetu@gmail.com',
  readOnlyAdmin: 'sdmalw.alw@gmail.com',
  /** Employer-side HR Head account (delegated hiring manager). */
  hrHead: 'hr.head@example.com',
  /** Brand-new job seeker: no profile yet, so the onboarding/fill flow shows. */
  newCandidate: 'newcandidate@example.com',
  /** Brand-new employer: no profile yet → must complete profile + await verification. */
  newEmployer: 'newemployer@example.com',
} as const;

const daysAgo = (n: number): string => new Date(Date.now() - n * 86_400_000).toISOString();

export function createSeedDb(): MockDb {
  const users: MockUser[] = [
    { userId: 'u-admin', email: DEMO_EMAILS.admin, role: 'admin', profileCompleted: true, isActive: true, name: 'Alwar Rojgar Setu Admin', adminStatus: 'approved', adminRole: 'super_admin', createdAt: daysAgo(120) },
    { userId: 'u-admin-dic', email: 'dicalwar@rajasthan.gov.in', role: 'admin', profileCompleted: true, isActive: true, name: 'Harish Mittal', adminStatus: 'approved', adminRole: 'super_admin', createdAt: daysAgo(90) },
    { userId: 'u-admin-oic', email: DEMO_EMAILS.readOnlyAdmin, role: 'admin', profileCompleted: true, isActive: true, name: 'Shree Madhav Bharadwaj', adminStatus: 'approved', adminRole: 'read_only', createdAt: daysAgo(85) },
    { userId: 'u-admin-riico', email: 'alwar@riico.co.in', role: 'admin', profileCompleted: true, isActive: true, name: 'Paresh Saxena', adminStatus: 'approved', adminRole: 'read_only', createdAt: daysAgo(80) },
    { userId: 'u-admin-iti', email: 'iti_alwar_govt@yahoo.co.in', role: 'admin', profileCompleted: true, isActive: true, name: 'Manoj Aggrawal', adminStatus: 'approved', adminRole: 'read_only', createdAt: daysAgo(75) },
    { userId: 'u-admin-deo', email: 'deo.alw.emp@rajasthan.gov.in', role: 'admin', profileCompleted: true, isActive: true, name: 'Harish Nainakwal', adminStatus: 'approved', adminRole: 'read_only', createdAt: daysAgo(70) },
    { userId: 'u-c1', email: DEMO_EMAILS.candidate, role: 'candidate', profileCompleted: true, isActive: true },
    // New job seeker with no profile yet — used to demo the "fill your profile" flow.
    { userId: 'u-c-new', email: DEMO_EMAILS.newCandidate, role: 'candidate', profileCompleted: false, isActive: true },
    { userId: 'u-c2', email: 'priya.verma@example.com', role: 'candidate', profileCompleted: true, isActive: true },
    { userId: 'u-c3', email: 'amit.kumar@example.com', role: 'candidate', profileCompleted: true, isActive: true },
    { userId: 'u-c4', email: 'sunil.yadav@example.com', role: 'candidate', profileCompleted: true, isActive: false },
    { userId: 'u-c5', email: 'kavita.meena@example.com', role: 'candidate', profileCompleted: true, isActive: true },
    { userId: 'u-c6', email: 'deepa.sharma@example.com', role: 'candidate', profileCompleted: true, isActive: true },
    { userId: 'u-c7', email: 'ranjit.singh@example.com', role: 'candidate', profileCompleted: true, isActive: true },
    { userId: 'u-e1', email: DEMO_EMAILS.employer, role: 'employer', profileCompleted: true, isActive: true },
    // New employer with no profile yet — demos the "complete profile → await verification → post jobs" flow.
    { userId: 'u-e-new', email: DEMO_EMAILS.newEmployer, role: 'employer', profileCompleted: false, isActive: true },
    { userId: 'u-e2', email: 'contact@tijara-textiles.com', role: 'employer', profileCompleted: true, isActive: true },
    { userId: 'u-e3', email: 'contact@alwar-engineering.com', role: 'employer', profileCompleted: true, isActive: true },
  ];

  const candidateProfiles: CandidateProfile[] = [
    {
      id: 'cp-1',
      userId: 'u-c1',
      fullName: 'Rahul Sharma',
      gender: 'male',
      phone: '9811100001',
      email: 'rahul.sharma@example.com',
      description:
        'ITI Fitter with 1+ year of assembly-line experience. Comfortable with blueprint reading and basic welding. Seeking a stable role near Alwar.',
      highestEducation: 'ITI',
      itiTrade: 'Fitter',
      itiCollege: 'Govt. ITI Alwar',
      department: 'Mechanical',
      graduationYear: 2023,
      workExperienceMonths: 14,
      skills: ['Fitter', 'Blueprint Reading', 'Welding Basics'],
      workExperiences: [
        {
          company: 'Alwar Engineering Works',
          role: 'Fitter',
          fromMonth: '2023-06',
          toMonth: null,
          current: true,
          description: 'Assembly-line fitting, blueprint reading, basic welding.',
        },
      ],
      educations: [
        { degree: 'ITI', institution: 'Govt. ITI Alwar', field: 'Fitter', year: 2023 },
      ],
      city: 'Alwar',
      district: 'Alwar',
      pincode: '301001',
      resumeUrl: 'mock://uploads/rahul-resume.pdf',
      aadhaarVerified: true,
      isActive: true,
      createdAt: daysAgo(40),
    },
    {
      id: 'cp-2',
      userId: 'u-c2',
      fullName: 'Priya Verma',
      gender: 'female',
      phone: '9811100002',
      description:
        'Fresh ITI Electrician graduate skilled in domestic and industrial wiring and COPA basics. Eager to learn on the job.',
      highestEducation: 'ITI',
      itiTrade: 'Electrician',
      itiCollege: 'Govt. ITI Bhiwadi',
      department: 'Electrical',
      graduationYear: 2025,
      workExperienceMonths: 0,
      skills: ['Electrician', 'Wiring', 'COPA'],
      city: 'Bhiwadi',
      district: 'Bhiwadi',
      pincode: '301019',
      aadhaarVerified: true,
      isActive: true,
      createdAt: daysAgo(20),
    },
    {
      id: 'cp-3',
      userId: 'u-c3',
      fullName: 'Amit Kumar',
      gender: 'male',
      phone: '9811100003',
      description:
        'Diploma-qualified Diesel Mechanic with 2+ years overhauling heavy-vehicle engines. Reliable and safety-conscious.',
      highestEducation: 'Diploma',
      itiTrade: 'Diesel Mechanic',
      itiCollege: 'Govt. ITI Tijara',
      department: 'Automobile',
      graduationYear: 2021,
      workExperienceMonths: 28,
      skills: ['Diesel Mechanic', 'Engine Overhaul'],
      city: 'Tijara',
      district: 'Tijara',
      pincode: '301411',
      aadhaarVerified: false,
      isActive: true,
      createdAt: daysAgo(12),
    },
    {
      id: 'cp-5',
      userId: 'u-c5',
      fullName: 'Kavita Meena',
      gender: 'female',
      phone: '9811100006',
      description: 'COPA-trained computer operator with typing and data-entry skills. Seeking office or banking role near Alwar.',
      highestEducation: 'ITI',
      itiTrade: 'COPA (Computer Operator)',
      itiCollege: 'Govt. ITI Alwar',
      department: 'IT & ITES',
      graduationYear: 2024,
      workExperienceMonths: 4,
      skills: ['COPA', 'MS Office', 'Data Entry'],
      city: 'Alwar',
      district: 'Alwar',
      pincode: '301001',
      aadhaarVerified: true,
      isActive: true,
      createdAt: daysAgo(15),
    },
    {
      id: 'cp-6',
      userId: 'u-c6',
      fullName: 'Deepa Sharma',
      gender: 'female',
      phone: '9811100007',
      description: 'Diploma in Electronics with 1 year of quality-control experience in a PCB manufacturing unit.',
      highestEducation: 'Diploma',
      itiTrade: 'Electronics',
      itiCollege: 'Govt. Polytechnic Alwar',
      department: 'Electronics',
      graduationYear: 2023,
      workExperienceMonths: 12,
      skills: ['Electronics', 'QC', 'PCB Testing'],
      city: 'Alwar',
      district: 'Alwar',
      pincode: '301001',
      aadhaarVerified: true,
      isActive: true,
      createdAt: daysAgo(10),
    },
    {
      id: 'cp-7',
      userId: 'u-c7',
      fullName: 'Ranjit Singh',
      gender: 'male',
      phone: '9811100008',
      description: 'Turner with 2 years on CNC and conventional lathes. Experienced in precision component manufacturing.',
      highestEducation: 'ITI',
      itiTrade: 'Turner',
      itiCollege: 'Govt. ITI Bhiwadi',
      department: 'Mechanical',
      graduationYear: 2022,
      workExperienceMonths: 24,
      skills: ['Turner', 'CNC Lathe', 'Precision Machining'],
      city: 'Bhiwadi',
      district: 'Bhiwadi',
      pincode: '301019',
      aadhaarVerified: false,
      isActive: true,
      createdAt: daysAgo(6),
    },
    {
      id: 'cp-4',
      userId: 'u-c4',
      fullName: 'Sunil Yadav',
      gender: 'male',
      phone: '9811100005',
      description:
        'ITI Welder trained in arc and MIG welding. 6 months of fabrication-shop experience; keen to grow with a manufacturing unit.',
      highestEducation: 'ITI',
      itiTrade: 'Welder',
      itiCollege: 'Govt. ITI Alwar',
      department: 'Welding & Fabrication',
      graduationYear: 2024,
      workExperienceMonths: 6,
      skills: ['Welder', 'Arc Welding'],
      city: 'Alwar',
      district: 'Alwar',
      pincode: '301001',
      aadhaarVerified: true,
      isActive: false,
      createdAt: daysAgo(8),
    },
  ];

  const employerProfiles: EmployerProfile[] = [
    {
      id: 'ep-1',
      userId: 'u-e1',
      companyName: 'Bhiwadi Auto Components Pvt. Ltd.',
      description: 'Manufacturing auto components for leading automotive brands across India.',
      employerRole: 'owner',
      contactPersonName: 'Rakesh Gupta',
      contactPersonDesignation: 'Director',
      contactPersonPhone: '9829010001',
      contactPersonEmail: 'rakesh@bhiwadiauto.example.com',
      altContactPersonName: 'Sunita Rao',
      altContactPersonPhone: '9829019999',
      gstNumber: '08ABCDE1234F1Z5',
      udyamNumber: 'UDYAM-RJ-01-0001234',
      status: 'verified',
      verifiedBy: 'u-admin',
      verifiedAt: daysAgo(30),
      isActive: true,
      createdAt: daysAgo(60),
    },
    {
      id: 'ep-hr',
      userId: 'u-e2',
      companyName: 'Shakti Electricals Pvt. Ltd.',
      employerRole: 'owner',
      contactPersonName: 'Neha Singhal',
      contactPersonDesignation: 'HR Manager',
      contactPersonPhone: '9829010002',
      contactPersonEmail: 'neha.hr@shaktielectricals.example.com',
      gstNumber: '08SHAKT5678E1Z3',
      udyamNumber: 'UDYAM-RJ-01-0009911',
      status: 'verified',
      verifiedBy: 'u-admin',
      verifiedAt: daysAgo(22),
      isActive: true,
      createdAt: daysAgo(45),
    },
    {
      id: 'ep-2',
      userId: 'u-e2',
      companyName: 'Tijara Textiles Pvt. Ltd.',
      employerRole: 'owner',
      contactPersonName: 'Anjali Gupta',
      contactPersonDesignation: 'Proprietor',
      contactPersonPhone: '9829010003',
      contactPersonEmail: 'anjali@tijaratextiles.example.com',
      gstNumber: '08ZYXWV9876C1Z2',
      udyamNumber: 'UDYAM-RJ-01-0007788',
      status: 'pending',
      isActive: true,
      createdAt: daysAgo(3),
    },
    {
      id: 'ep-3',
      userId: 'u-e3',
      companyName: 'Alwar Engineering Works',
      employerRole: 'owner',
      contactPersonName: 'Mahesh Saini',
      contactPersonDesignation: 'Owner',
      contactPersonPhone: '9829010004',
      contactPersonEmail: 'mahesh@alwarengg.example.com',
      gstNumber: '08LMNOP4567Q1Z9',
      status: 'verified',
      verifiedBy: 'u-admin',
      verifiedAt: daysAgo(25),
      isActive: true,
      createdAt: daysAgo(50),
    },
    {
      id: 'ep-havells',
      userId: 'u-e1',
      companyName: 'Havells India Ltd.',
      employerRole: 'owner',
      contactPersonName: 'Manoj Verma',
      contactPersonDesignation: 'Plant HR',
      contactPersonPhone: '9829010005',
      contactPersonEmail: 'manoj.verma@havells.example.com',
      gstNumber: '08HAVLS1234A1Z1',
      status: 'verified',
      verifiedBy: 'u-admin',
      verifiedAt: daysAgo(45),
      isActive: true,
      createdAt: daysAgo(90),
    },
    {
      id: 'ep-hero',
      userId: 'u-e2',
      companyName: 'Hero MotoCorp',
      employerRole: 'owner',
      contactPersonName: 'Suresh Rana',
      contactPersonDesignation: 'HR Manager',
      contactPersonPhone: '9829010006',
      contactPersonEmail: 'suresh.rana@heromotocorp.example.com',
      gstNumber: '08HERMC5678B1Z2',
      status: 'verified',
      verifiedBy: 'u-admin',
      verifiedAt: daysAgo(40),
      isActive: true,
      createdAt: daysAgo(85),
    },
    {
      id: 'ep-rspl',
      userId: 'u-e3',
      companyName: 'RSPL Ltd. (Ghari)',
      employerRole: 'owner',
      contactPersonName: 'Deepak Sharma',
      contactPersonDesignation: 'HR Executive',
      contactPersonPhone: '9829010007',
      contactPersonEmail: 'deepak.sharma@rspl.example.com',
      gstNumber: '08RSPLG9012C1Z3',
      status: 'verified',
      verifiedBy: 'u-admin',
      verifiedAt: daysAgo(35),
      isActive: true,
      createdAt: daysAgo(80),
    },
    {
      id: 'ep-aubank',
      userId: 'u-e1',
      companyName: 'AU Small Finance Bank',
      employerRole: 'owner',
      contactPersonName: 'Kavita Meena',
      contactPersonDesignation: 'Zonal HR',
      contactPersonPhone: '9829010008',
      contactPersonEmail: 'kavita.meena@aubank.example.com',
      gstNumber: '08AUSFB3456D1Z4',
      status: 'verified',
      verifiedBy: 'u-admin',
      verifiedAt: daysAgo(30),
      isActive: true,
      createdAt: daysAgo(70),
    },
    {
      id: 'ep-hdfc',
      userId: 'u-e2',
      companyName: 'HDFC Bank',
      employerRole: 'owner',
      contactPersonName: 'Pankaj Joshi',
      contactPersonDesignation: 'Branch HR',
      contactPersonPhone: '9829010009',
      contactPersonEmail: 'pankaj.joshi@hdfcbank.example.com',
      gstNumber: '08HDFCB7890E1Z5',
      status: 'verified',
      verifiedBy: 'u-admin',
      verifiedAt: daysAgo(28),
      isActive: true,
      createdAt: daysAgo(65),
    },
  ];

  const employerDocuments: EmployerDocument[] = [
    {
      id: 'doc-1',
      employerId: 'ep-1',
      documentType: 'GST_CERTIFICATE',
      documentUrl: 'mock://uploads/ep-1-gst.pdf',
      verificationStatus: 'verified',
      uploadedAt: daysAgo(58),
      verifiedAt: daysAgo(30),
      verifiedBy: 'u-admin',
    },
    {
      id: 'doc-2',
      employerId: 'ep-1',
      documentType: 'UDYAM_CERTIFICATE',
      documentUrl: 'mock://uploads/ep-1-udyam.pdf',
      verificationStatus: 'verified',
      uploadedAt: daysAgo(58),
      verifiedAt: daysAgo(30),
      verifiedBy: 'u-admin',
    },
  ];

  const baseJob = (j: Partial<Job> & Pick<Job, 'id' | 'employerId' | 'title' | 'companyName'>): Job => ({
    description:
      'We are hiring for our Alwar/Bhiwadi industrial unit. Stable shift timings, ESI/PF benefits, and on-the-job training provided. Candidates from local ITI/Diploma backgrounds preferred.',
    grossSalary: 22000,
    netSalary: 19500,
    jobType: 'permanent',
    openings: 2,
    filledCount: 0,
    district: 'Alwar',
    status: 'active',
    postedAt: daysAgo(5),
    createdAt: daysAgo(5),
    ...j,
  });

  const jobs: Job[] = [
    baseJob({
      id: 'job-1',
      employerId: 'ep-1',
      companyName: 'Bhiwadi Auto Components Pvt. Ltd.',
      title: 'ITI Fitter – Assembly Line',
      tradeRequired: 'Fitter',
      grossSalary: 22000,
      netSalary: 19500,
      openings: 5,
      filledCount: 1,
      district: 'Alwar',
      postedAt: daysAgo(2),
      createdAt: daysAgo(2),
    }),
    baseJob({
      id: 'job-2',
      employerId: 'ep-1',
      companyName: 'Bhiwadi Auto Components Pvt. Ltd.',
      title: 'Electrician (Maintenance)',
      tradeRequired: 'Electrician',
      jobType: 'contract',
      grossSalary: 20000,
      netSalary: 18000,
      openings: 3,
      district: 'Bhiwadi',
      postedAt: daysAgo(6),
      createdAt: daysAgo(6),
    }),
    baseJob({
      id: 'job-3',
      employerId: 'ep-1',
      companyName: 'Bhiwadi Auto Components Pvt. Ltd.',
      title: 'Welder – Fabrication Shop',
      tradeRequired: 'Welder',
      grossSalary: 24000,
      netSalary: 21000,
      openings: 2,
      district: 'Alwar',
      postedAt: daysAgo(9),
      createdAt: daysAgo(9),
    }),
    baseJob({
      id: 'job-4',
      employerId: 'ep-3',
      companyName: 'Alwar Engineering Works',
      title: 'Diesel Mechanic',
      tradeRequired: 'Diesel Mechanic',
      grossSalary: 26000,
      netSalary: 23000,
      openings: 2,
      district: 'Tijara',
      postedAt: daysAgo(4),
      createdAt: daysAgo(4),
    }),
    baseJob({
      id: 'job-5',
      employerId: 'ep-3',
      companyName: 'Alwar Engineering Works',
      title: 'Computer Operator (COPA) – Trainee',
      tradeRequired: 'COPA (Computer Operator)',
      jobType: 'internship',
      grossSalary: 16000,
      netSalary: 15000,
      openings: 2,
      district: 'Alwar',
      postedAt: daysAgo(1),
      createdAt: daysAgo(1),
    }),
    baseJob({
      id: 'job-6',
      employerId: 'ep-1',
      companyName: 'Bhiwadi Auto Components Pvt. Ltd.',
      title: 'Machinist (CNC)',
      tradeRequired: 'Machinist',
      grossSalary: 23000,
      netSalary: 20500,
      openings: 1,
      district: 'Bhiwadi',
      status: 'closed',
      postedAt: daysAgo(20),
      createdAt: daysAgo(20),
    }),
    baseJob({
      id: 'job-7',
      employerId: 'ep-hr',
      companyName: 'Shakti Electricals Pvt. Ltd.',
      title: 'Electrician – Panel Assembly',
      tradeRequired: 'Electrician',
      grossSalary: 21000,
      netSalary: 18500,
      openings: 4,
      district: 'Bhiwadi',
      postedAt: daysAgo(3),
      createdAt: daysAgo(3),
    }),
    baseJob({
      id: 'job-8',
      employerId: 'ep-hr',
      companyName: 'Shakti Electricals Pvt. Ltd.',
      title: 'Wireman – Switchgear Unit',
      tradeRequired: 'Wireman',
      grossSalary: 19000,
      netSalary: 17000,
      openings: 3,
      district: 'Alwar',
      postedAt: daysAgo(7),
      createdAt: daysAgo(7),
    }),
    // --- Havells India Ltd. ---
    baseJob({
      id: 'job-havells-1',
      employerId: 'ep-havells',
      companyName: 'Havells India Ltd.',
      title: 'Electrician – Appliance Assembly',
      tradeRequired: 'Electrician',
      grossSalary: 23000,
      netSalary: 20500,
      openings: 8,
      district: 'Alwar',
      postedAt: daysAgo(1),
      createdAt: daysAgo(1),
    }),
    baseJob({
      id: 'job-havells-2',
      employerId: 'ep-havells',
      companyName: 'Havells India Ltd.',
      title: 'Fitter – Motor Assembly Line',
      tradeRequired: 'Fitter',
      grossSalary: 21000,
      netSalary: 18800,
      openings: 6,
      district: 'Alwar',
      postedAt: daysAgo(3),
      createdAt: daysAgo(3),
    }),
    baseJob({
      id: 'job-havells-3',
      employerId: 'ep-havells',
      companyName: 'Havells India Ltd.',
      title: 'Inventory Executive (Graduate)',
      description: 'Manage warehouse inventory, billing, and dispatch records. Knowledge of ERP/Tally preferred. Freshers welcome.',
      grossSalary: 25000,
      netSalary: 22000,
      openings: 2,
      district: 'Alwar',
      postedAt: daysAgo(2),
      createdAt: daysAgo(2),
    }),
    // --- Hero MotoCorp ---
    baseJob({
      id: 'job-hero-1',
      employerId: 'ep-hero',
      companyName: 'Hero MotoCorp',
      title: 'Fitter – Automotive Assembly',
      tradeRequired: 'Fitter',
      grossSalary: 24000,
      netSalary: 21500,
      openings: 10,
      district: 'Alwar',
      postedAt: daysAgo(2),
      createdAt: daysAgo(2),
    }),
    baseJob({
      id: 'job-hero-2',
      employerId: 'ep-hero',
      companyName: 'Hero MotoCorp',
      title: 'Welder – Chassis Fabrication',
      tradeRequired: 'Welder',
      grossSalary: 25000,
      netSalary: 22000,
      openings: 4,
      district: 'Alwar',
      postedAt: daysAgo(4),
      createdAt: daysAgo(4),
    }),
    baseJob({
      id: 'job-hero-3',
      employerId: 'ep-hero',
      companyName: 'Hero MotoCorp',
      title: 'Parts Coordinator (Graduate)',
      description: 'Coordinate spare parts procurement, vendor management, and logistics. Graduate with basic computer skills required.',
      grossSalary: 26000,
      netSalary: 23000,
      openings: 3,
      district: 'Alwar',
      postedAt: daysAgo(1),
      createdAt: daysAgo(1),
    }),
    // --- RSPL Ltd. (Ghari) ---
    baseJob({
      id: 'job-rspl-1',
      employerId: 'ep-rspl',
      companyName: 'RSPL Ltd. (Ghari)',
      title: 'Machine Operator – Packaging',
      tradeRequired: 'Fitter',
      grossSalary: 20000,
      netSalary: 17800,
      openings: 12,
      district: 'Alwar',
      postedAt: daysAgo(1),
      createdAt: daysAgo(1),
    }),
    baseJob({
      id: 'job-rspl-2',
      employerId: 'ep-rspl',
      companyName: 'RSPL Ltd. (Ghari)',
      title: 'Electrician – Plant Maintenance',
      tradeRequired: 'Electrician',
      grossSalary: 22000,
      netSalary: 19500,
      openings: 3,
      district: 'Alwar',
      postedAt: daysAgo(5),
      createdAt: daysAgo(5),
    }),
    baseJob({
      id: 'job-rspl-3',
      employerId: 'ep-rspl',
      companyName: 'RSPL Ltd. (Ghari)',
      title: 'Supply Chain Assistant (Graduate)',
      description: 'Assist supply chain team with dispatch planning, vehicle tracking, and delivery confirmation. Freshers welcome.',
      grossSalary: 22000,
      netSalary: 19500,
      openings: 2,
      district: 'Alwar',
      postedAt: daysAgo(3),
      createdAt: daysAgo(3),
    }),
    // --- AU Small Finance Bank ---
    baseJob({
      id: 'job-aubank-1',
      employerId: 'ep-aubank',
      companyName: 'AU Small Finance Bank',
      title: 'Customer Service Executive',
      description: 'Handle counter transactions, assist customers with account queries, and promote bank products. Graduate required.',
      grossSalary: 28000,
      netSalary: 24500,
      openings: 4,
      district: 'Alwar',
      postedAt: daysAgo(2),
      createdAt: daysAgo(2),
    }),
    baseJob({
      id: 'job-aubank-2',
      employerId: 'ep-aubank',
      companyName: 'AU Small Finance Bank',
      title: 'CASA Sales Officer',
      description: 'Acquire new savings and current accounts through field visits and referrals. Graduate with good communication skills.',
      grossSalary: 30000,
      netSalary: 26000,
      openings: 6,
      district: 'Alwar',
      postedAt: daysAgo(1),
      createdAt: daysAgo(1),
    }),
    // --- HDFC Bank ---
    baseJob({
      id: 'job-hdfc-1',
      employerId: 'ep-hdfc',
      companyName: 'HDFC Bank',
      title: 'Branch Banking Officer',
      description: 'Manage daily banking operations including account openings, cheque clearings, and customer service at the Alwar branch.',
      grossSalary: 32000,
      netSalary: 28000,
      openings: 2,
      district: 'Alwar',
      postedAt: daysAgo(3),
      createdAt: daysAgo(3),
    }),
    baseJob({
      id: 'job-hdfc-2',
      employerId: 'ep-hdfc',
      companyName: 'HDFC Bank',
      title: 'Loan Verification Executive',
      description: 'Conduct field verification for home and personal loan applicants. Two-wheeler and smartphone required. Graduate preferred.',
      jobType: 'contract',
      grossSalary: 25000,
      netSalary: 22000,
      openings: 3,
      district: 'Alwar',
      postedAt: daysAgo(2),
      createdAt: daysAgo(2),
    }),
  ];

  const applications: Application[] = [
    { id: 'app-1', candidateId: 'cp-1', jobId: 'job-1', status: 'viewed', viewedAt: daysAgo(1), createdAt: daysAgo(2) },
    { id: 'app-2', candidateId: 'cp-1', jobId: 'job-3', status: 'shortlisted', viewedAt: daysAgo(7), shortlistedAt: daysAgo(6), createdAt: daysAgo(8) },
    { id: 'app-3', candidateId: 'cp-2', jobId: 'job-1', status: 'received', createdAt: daysAgo(1) },
    { id: 'app-4', candidateId: 'cp-3', jobId: 'job-4', status: 'received', createdAt: daysAgo(3) },
    { id: 'app-5', candidateId: 'cp-4', jobId: 'job-3', status: 'received', createdAt: daysAgo(5) },
    { id: 'app-6', candidateId: 'cp-2', jobId: 'job-2', status: 'received', createdAt: daysAgo(4) },
    { id: 'app-8', candidateId: 'cp-5', jobId: 'job-5', status: 'received', createdAt: daysAgo(3) },
    { id: 'app-9', candidateId: 'cp-6', jobId: 'job-5', status: 'shortlisted', viewedAt: daysAgo(5), shortlistedAt: daysAgo(3), createdAt: daysAgo(7) },
    {
      id: 'app-10',
      candidateId: 'cp-5',
      jobId: 'job-aubank-1',
      status: 'hired',
      viewedAt: daysAgo(12),
      shortlistedAt: daysAgo(10),
      hiredAt: daysAgo(7),
      attributedToPlatform: true,
      joiningDate: daysAgo(3),
      createdAt: daysAgo(14),
    },
    {
      id: 'app-11',
      candidateId: 'cp-1',
      jobId: 'job-hero-1',
      status: 'hired',
      viewedAt: daysAgo(20),
      shortlistedAt: daysAgo(18),
      hiredAt: daysAgo(14),
      attributedToPlatform: true,
      joiningDate: daysAgo(10),
      createdAt: daysAgo(22),
    },
    {
      id: 'app-12',
      candidateId: 'cp-3',
      jobId: 'job-4',
      status: 'hired',
      viewedAt: daysAgo(15),
      shortlistedAt: daysAgo(12),
      hiredAt: daysAgo(9),
      attributedToPlatform: false,
      joiningDate: daysAgo(5),
      createdAt: daysAgo(18),
    },
    {
      id: 'app-7',
      candidateId: 'cp-2',
      jobId: 'job-7',
      status: 'hired',
      viewedAt: daysAgo(10),
      shortlistedAt: daysAgo(9),
      hiredAt: daysAgo(6),
      attributedToPlatform: true,
      joiningDate: daysAgo(1),
      createdAt: daysAgo(12),
    },
  ];

  return {
    users,
    candidateProfiles,
    employerProfiles,
    employerDocuments,
    jobs,
    applications,
    sessionUserId: null,
    pendingRole: {},
    // Emails granted admin access ahead of time — demoes the "pending invites" list.
    adminInvites: [
      {
        id: 'invite-1',
        email: 'neha.sharma@example.com',
        invitedByName: 'District Admin',
        invitedByEmail: DEMO_EMAILS.admin,
        createdAt: daysAgo(3),
      },
    ],
    testimonials: [
      {
        id: 'tm-1',
        candidateId: 'cp-1',
        name: 'Rahul Sharma',
        photoUrl: null,
        trade: 'Fitter',
        body: 'Mujhe yahan se 3 mahine mein Havells mein job mil gayi. Bahut achha platform hai, bilkul seedha aur free.',
        isPublished: true,
        displayOrder: 0,
        createdAt: daysAgo(30),
        updatedAt: daysAgo(30),
      },
      {
        id: 'tm-2',
        candidateId: 'cp-2',
        name: 'Priya Verma',
        photoUrl: null,
        trade: 'Electrician',
        body: 'Maine pehle bahut jagah try kiya tha, par yahan se seedha Alwar ke pass job mili. Ab salary bhi achhi hai.',
        isPublished: true,
        displayOrder: 1,
        createdAt: daysAgo(20),
        updatedAt: daysAgo(20),
      },
      {
        id: 'tm-3',
        candidateId: null,
        name: 'Amit Kumar',
        photoUrl: null,
        trade: 'Welder',
        body: 'Is platform ki wajah se mujhe ghar ke paas hi permanent job mili. Parivaar bhi khush hai.',
        isPublished: true,
        displayOrder: 2,
        createdAt: daysAgo(10),
        updatedAt: daysAgo(10),
      },
      {
        id: 'tm-4',
        candidateId: 'cp-5',
        name: 'Kavita Meena',
        photoUrl: null,
        trade: 'COPA',
        body: 'Mujhe lagta tha ladkiyon ko yahan job nahi milti, par is portal ne mujhe AU Bank mein job dilai. Ab main apne parivar ka sahara hoon.',
        isPublished: true,
        displayOrder: 3,
        createdAt: daysAgo(7),
        updatedAt: daysAgo(7),
      },
      {
        id: 'tm-5',
        candidateId: null,
        name: 'Suresh Yadav',
        photoUrl: null,
        trade: 'Electrician',
        body: 'Bahut aasaan process tha. Maine profile banai, ek hafte mein interview call aaya aur 15 din mein joining ho gayi Shakti Electricals mein.',
        isPublished: true,
        displayOrder: 4,
        createdAt: daysAgo(25),
        updatedAt: daysAgo(25),
      },
      {
        id: 'tm-6',
        candidateId: null,
        name: 'Neetu Sharma',
        photoUrl: null,
        trade: 'Electronics',
        body: 'Maine kai baar suna tha ke sarkari portals kaam nahi karte, par yeh platform sach mein helpful hai. Meri job Bhiwadi mein lagi aur salary bhi achhi hai.',
        isPublished: true,
        displayOrder: 5,
        createdAt: daysAgo(18),
        updatedAt: daysAgo(18),
      },
      {
        id: 'tm-7',
        candidateId: 'cp-3',
        name: 'Amit Kumar',
        photoUrl: null,
        trade: 'Diesel Mechanic',
        body: 'Alwar Engineering Works mein job mile 2 mahine ho gaye. Platform pe profile upload ki aur directly call aaya. Koi agent nahi, koi fee nahi.',
        isPublished: true,
        displayOrder: 6,
        createdAt: daysAgo(60),
        updatedAt: daysAgo(60),
      },
    ] as Testimonial[],
  };
}
